import React from 'react';
import { ExclamationCircleFilled, ReloadOutlined } from '@ant-design/icons';
import { Tooltip } from '../../Tooltip';
import { useFilterBarContext } from '../state/FilterBarContext';
import { useFilterField } from '../hooks/useFilterField';
import { SingleSelectFilterControl } from '../controls/SingleSelectFilterControl';
import { MultiSelectFilterControl } from '../controls/MultiSelectFilterControl';
import { TextSearchFilterControl } from '../controls/TextSearchFilterControl';
import { SegmentToggleFilterControl } from '../controls/SegmentToggleFilterControl';
import { DateRangeFilterControl } from '../controls/DateRangeFilterControl';
import { NumericRangeFilterControl } from '../controls/NumericRangeFilterControl';
import type { FilterItemConfig } from '../types';

import cn from './styles.module.scss';

export interface FilterItemProps {
  name: string;
}

/**
 * Renders a single filter's control directly in the bar. The field's `label`
 * becomes the control's placeholder (or a static prefix for SELECT variants),
 * so the visual is just the bare control sitting in the row — no extra click to
 * reveal or edit a value.
 *
 * On ERROR the control is replaced by an inline error row (icon + message +
 * Reload). SEGMENT_TOGGLE is self-describing via its options, so it gets
 * neither placeholder nor prefix.
 */
export const FilterItem: React.FC<FilterItemProps> = ({ name }) => {
  const { controller, labels } = useFilterBarContext();
  const { phase, error, config } = useFilterField(name);

  if (!config) return null;
  const isError = phase === 'ERROR';

  return (
    <div
      className={cn.item}
      data-testid={`filter-item-${name}`}
    >
      {isError ? (
        <ErrorRow
          fieldLabel={config.label}
          message={error ?? labels.loadFailed}
          reloadLabel={labels.reload}
          onReload={() => controller.reload(name)}
        />
      ) : (
        renderControl(config.type, name, config.label, config.placeholder)
      )}
    </div>
  );
};

const ErrorRow: React.FC<{
  fieldLabel: string;
  message: string;
  reloadLabel: string;
  onReload: () => void;
}> = ({ fieldLabel, message, reloadLabel, onReload }) => (
  <div className={cn.errorRow}>
    <Tooltip title={message}>
      <span className={cn.errorText}>
        <ExclamationCircleFilled className={cn.errorIcon} /> {fieldLabel}: {message}
      </span>
    </Tooltip>
    <button type="button" className={cn.errorReload} onClick={onReload}>
      <ReloadOutlined /> {reloadLabel}
    </button>
  </div>
);

/**
 * Per-type control render:
 *   - SELECT / SELECT_MULTI: render the label as a static **prefix** next to
 *     the control. Placeholder text disappears once a value is selected, so
 *     a prefix is needed to keep the field's identity visible.
 *   - Everything else: the label becomes the control's placeholder.
 *   - SEGMENT_TOGGLE is self-describing via its options and gets neither.
 */
function renderControl(
  type: FilterItemConfig['type'],
  name: string,
  label: string,
  placeholder: string | undefined,
): React.ReactNode {
  switch (type) {
    case 'SELECT':
      return <SingleSelectFilterControl name={name} prefix={label} placeholder={placeholder} />;
    case 'SELECT_MULTI':
      return <MultiSelectFilterControl name={name} prefix={label} placeholder={placeholder} />;
    case 'TEXT_SEARCH':
      return <TextSearchFilterControl name={name} placeholder={placeholder ?? label} />;
    case 'SEGMENT_TOGGLE':
      // Segmented toggle has no placeholder concept. Options carry meaning.
      return <SegmentToggleFilterControl name={name} />;
    case 'DATE_RANGE':
      return <DateRangeFilterControl name={name} placeholder={placeholder ?? label} />;
    case 'NUMERIC_RANGE':
      return <NumericRangeFilterControl name={name} placeholder={placeholder ?? label} />;
  }
}
